<script setup>
import { formatDate, formatNumber, formatRupiah } from '@/composables/useFormat'
import { useApi } from '@/utils/api'; // ← GANTI dari $api ke useApi

definePage({
  meta: {
    layout: 'default',
    action: 'read',
    subject: 'Report',
  },
})

// ── State ─────────────────────────────────────────────────────────────────────
const loading     = ref(false)
const dateFrom    = ref(new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().slice(0, 10))
const dateTo      = ref(new Date().toISOString().slice(0, 10))
const groupBy     = ref('day')

const report      = ref(null)
const periods     = ref([])
const totals      = ref({})
const topProducts = ref([])
const paymentBreakdown = ref([])

const groupByOptions = [
  { title: 'Harian', value: 'day' },
  { title: 'Mingguan', value: 'week' },
  { title: 'Bulanan', value: 'month' },
]

// ── Table Headers ──────────────────────────────────────────────────────────────
const periodHeaders = [
  { title: 'Periode', key: 'period', sortable: false },
  { title: 'Order', key: 'total_orders', align: 'end' },
  { title: 'Revenue', key: 'revenue', align: 'end' },
  { title: 'Diskon', key: 'discount', align: 'end' },
  { title: 'HPP', key: 'hpp', align: 'end' },
  { title: 'Laba Kotor', key: 'gross_profit', align: 'end' },
]

// ── Fetch ─────────────────────────────────────────────────────────────────────
const fetchReport = async () => {
  if (!dateFrom.value || !dateTo.value) return
  loading.value = true
  try {
    // ✅ FIX: Pakai useApi + URL tanpa prefix /api
    const response = await useApi('/v1/reports/sales', {
      query: {
        date_from: dateFrom.value,
        date_to:   dateTo.value,
        group_by:  groupBy.value,
      },
    }).get()
    
    const data = response?.data ?? response

    periods.value          = data?.periods          ?? []
    totals.value           = data?.totals            ?? {}
    topProducts.value      = data?.top_products     ?? []
    paymentBreakdown.value = data?.payment_breakdown ?? []
  } catch (err) {
    console.error('❌ Sales report error:', err)
  } finally {
    loading.value = false
  }
}

onMounted(fetchReport)

// ── Export ────────────────────────────────────────────────────────────────────
const exportFile = async (format) => {
  const params = new URLSearchParams({
    date_from: dateFrom.value,
    date_to:   dateTo.value,
    group_by:  groupBy.value,
  })
  const url = `/api/v1/export/sales/${format}?${params}`
  window.open(url, '_blank')
}

// ── Computed ──────────────────────────────────────────────────────────────────
const grossMargin = computed(() => {
  if (!totals.value.revenue) return 0
  return ((totals.value.gross_profit / totals.value.revenue) * 100).toFixed(1)
})

const avatarColors = ['primary', 'success', 'info', 'warning', 'error',
  'secondary', 'primary', 'success', 'info', 'warning']
</script>

<template>
  <div>
    <!-- Page Header -->
    <VRow class="mb-4 align-center">
      <VCol cols="12" md="6">
        <h4 class="text-h4 font-weight-medium mb-1">Laporan Penjualan</h4>
        <p class="text-body-2 text-medium-emphasis">Analisis penjualan berdasarkan periode</p>
      </VCol>
    </VRow>

    <!-- Filter Card -->
    <VCard class="mb-4">
      <VCardText>
        <VRow align="center">
          <VCol cols="12" sm="4" md="3">
            <VTextField
              v-model="dateFrom"
              type="date"
              label="Dari Tanggal"
              density="compact"
              hide-details
            />
          </VCol>
          <VCol cols="12" sm="4" md="3">
            <VTextField
              v-model="dateTo"
              type="date"
              label="Sampai Tanggal"
              density="compact"
              hide-details
            />
          </VCol>
          <VCol cols="12" sm="4" md="2">
            <VSelect
              v-model="groupBy"
              :items="groupByOptions"
              label="Kelompokkan"
              density="compact"
              hide-details
            />
          </VCol>
          <VCol cols="12" md="2">
            <VBtn
              color="primary"
              block
              :loading="loading"
              prepend-icon="ri-search-line"
              @click="fetchReport"
            >
              Tampilkan
            </VBtn>
          </VCol>
          <VCol cols="12" md="2">
            <VBtnGroup density="compact" variant="outlined" divided block>
              <VBtn
                prepend-icon="ri-file-excel-line"
                color="success"
                @click="exportFile('excel')"
              >
                Excel
              </VBtn>
              <VBtn
                prepend-icon="ri-file-pdf-line"
                color="error"
                @click="exportFile('pdf')"
              >
                PDF
              </VBtn>
            </VBtnGroup>
          </VCol>
        </VRow>
      </VCardText>
    </VCard>

    <!-- Empty state -->
    <VCard v-if="!loading && periods.length === 0" class="mb-4">
      <VCardText class="text-center py-12">
        <VIcon icon="ri-bar-chart-line" size="64" color="secondary" class="mb-3" />
        <p class="text-h6 font-weight-medium mb-1">Belum ada data</p>
        <p class="text-body-2 text-medium-emphasis">Pilih rentang tanggal dan klik Tampilkan</p>
      </VCardText>
    </VCard>

    <template v-else>
      <!-- Summary Cards -->
      <VRow class="mb-4">
        <VCol cols="6" sm="4" md="2">
          <VCard :loading="loading">
            <VCardText class="pa-3 text-center">
              <div class="text-caption text-medium-emphasis mb-1">Total Order</div>
              <div class="text-h6 font-weight-bold">{{ formatNumber(totals.total_orders) }}</div>
            </VCardText>
          </VCard>
        </VCol>
        <VCol cols="6" sm="4" md="2">
          <VCard :loading="loading">
            <VCardText class="pa-3 text-center">
              <div class="text-caption text-medium-emphasis mb-1">Revenue</div>
              <div class="text-body-1 font-weight-bold text-success">{{ formatRupiah(totals.revenue) }}</div>
            </VCardText>
          </VCard>
        </VCol>
        <VCol cols="6" sm="4" md="2">
          <VCard :loading="loading">
            <VCardText class="pa-3 text-center">
              <div class="text-caption text-medium-emphasis mb-1">Diskon</div>
              <div class="text-body-1 font-weight-bold text-warning">{{ formatRupiah(totals.discount) }}</div>
            </VCardText>
          </VCard>
        </VCol>
        <VCol cols="6" sm="4" md="2">
          <VCard :loading="loading">
            <VCardText class="pa-3 text-center">
              <div class="text-caption text-medium-emphasis mb-1">HPP</div>
              <div class="text-body-1 font-weight-bold text-error">{{ formatRupiah(totals.hpp) }}</div>
            </VCardText>
          </VCard>
        </VCol>
        <VCol cols="6" sm="4" md="2">
          <VCard :loading="loading">
            <VCardText class="pa-3 text-center">
              <div class="text-caption text-medium-emphasis mb-1">Laba Kotor</div>
              <div class="text-body-1 font-weight-bold text-info">{{ formatRupiah(totals.gross_profit) }}</div>
            </VCardText>
          </VCard>
        </VCol>
        <VCol cols="6" sm="4" md="2">
          <VCard :loading="loading">
            <VCardText class="pa-3 text-center">
              <div class="text-caption text-medium-emphasis mb-1">Gross Margin</div>
              <div class="text-body-1 font-weight-bold text-primary">{{ grossMargin }}%</div>
            </VCardText>
          </VCard>
        </VCol>
      </VRow>

      <!-- Period Table -->
      <VCard class="mb-4" :loading="loading">
        <VCardTitle class="text-body-1 font-weight-semibold pa-4 pb-2">
          Detail per Periode
        </VCardTitle>
        <VDivider />
        <VDataTable
          :headers="periodHeaders"
          :items="periods"
          :loading="loading"
          density="compact"
          hover
        >
          <template #item.period="{ item }">
            <span class="font-weight-medium">{{ formatDate(item.period) }}</span>
          </template>
          <template #item.total_orders="{ item }">
            {{ formatNumber(item.total_orders) }}
          </template>
          <template #item.revenue="{ item }">
            <span class="text-success font-weight-medium">{{ formatRupiah(item.revenue) }}</span>
          </template>
          <template #item.discount="{ item }">
            <span class="text-warning">({{ formatRupiah(item.discount) }})</span>
          </template>
          <template #item.hpp="{ item }">
            <span class="text-error">({{ formatRupiah(item.hpp) }})</span>
          </template>
          <template #item.gross_profit="{ item }">
            <span
              class="font-weight-bold"
              :class="item.gross_profit >= 0 ? 'text-info' : 'text-error'"
            >
              {{ formatRupiah(item.gross_profit) }}
            </span>
          </template>

          <!-- Footer total -->
          <template #bottom>
            <VDivider />
            <div class="pa-3 d-flex justify-end gap-6 flex-wrap">
              <span class="text-body-2 font-weight-bold">
                Total Order: {{ formatNumber(totals.total_orders) }}
              </span>
              <span class="text-body-2 font-weight-bold text-success">
                Revenue: {{ formatRupiah(totals.revenue) }}
              </span>
              <span class="text-body-2 font-weight-bold text-info">
                Laba Kotor: {{ formatRupiah(totals.gross_profit) }}
              </span>
            </div>
          </template>
        </VDataTable>
      </VCard>

      <!-- Bottom Row: Top Products + Payment Breakdown -->
      <VRow>
        <VCol cols="12" md="6">
          <VCard :loading="loading">
            <VCardTitle class="text-body-1 font-weight-semibold pa-4 pb-2">
              Top 10 Produk
            </VCardTitle>
            <VDivider />
            <VList density="compact">
              <VListItem
                v-for="(item, i) in topProducts"
                :key="i"
              >
                <template #prepend>
                  <VAvatar
                    :color="avatarColors[i]"
                    variant="tonal"
                    size="28"
                    class="text-caption font-weight-bold mr-3"
                  >
                    {{ i + 1 }}
                  </VAvatar>
                </template>
                <VListItemTitle class="text-body-2">
                  {{ item.product_name }}
                  <span v-if="item.variant_name" class="text-medium-emphasis"> · {{ item.variant_name }}</span>
                </VListItemTitle>
                <VListItemSubtitle class="text-caption">
                  {{ formatNumber(item.total_qty) }} terjual
                </VListItemSubtitle>
                <template #append>
                  <span class="text-body-2 text-success font-weight-medium">
                    {{ formatRupiah(item.total_revenue) }}
                  </span>
                </template>
              </VListItem>
            </VList>
          </VCard>
        </VCol>

        <VCol cols="12" md="6">
          <VCard :loading="loading">
            <VCardTitle class="text-body-1 font-weight-semibold pa-4 pb-2">
              Metode Pembayaran
            </VCardTitle>
            <VDivider />

            <VCardText v-if="paymentBreakdown.length === 0" class="text-center py-8">
              <p class="text-body-2 text-medium-emphasis">Tidak ada data pembayaran</p>
            </VCardText>

            <VList v-else density="compact">
              <VListItem
                v-for="item in paymentBreakdown"
                :key="item.method"
              >
                <template #prepend>
                  <VIcon
                    :icon="item.method === 'cash' ? 'ri-money-dollar-circle-line' : 'ri-bank-card-line'"
                    color="primary"
                    size="18"
                    class="mr-2"
                  />
                </template>
                <VListItemTitle class="text-body-2 text-capitalize">
                  {{ item.method }}
                </VListItemTitle>
                <VListItemSubtitle class="text-caption">
                  {{ formatNumber(item.total_orders) }} transaksi
                </VListItemSubtitle>
                <template #append>
                  <span class="text-body-2 font-weight-medium text-primary">
                    {{ formatRupiah(item.total) }}
                  </span>
                </template>
              </VListItem>
            </VList>
          </VCard>
        </VCol>
      </VRow>
    </template>
  </div>
</template>
